import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Grid,
  Avatar,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Palette as PaletteIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  PhotoCamera as CameraIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { RootState, useAppDispatch } from '../store';
import { updateUserPreferences } from '../store/slices/authSlice';
import { useIsAdmin } from '../hooks/useIsAdmin';
import {
  getAdminThresholds,
  updateAdminThresholds,
  getScoringConfig,
  updateScoringConfig,
  ThresholdSpec
} from '../services/adminService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * TabPanel component for the settings tabs
 */
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

/**
 * Settings page component
 * 
 * @returns React component
 */
const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { theme } = useSelector((state: RootState) => state.ui);
  
  const [activeTab, setActiveTab] = useState(0);
  const [darkMode, setDarkMode] = useState(theme === 'dark');
  const [enableNotifications, setEnableNotifications] = useState(
    user?.preferences?.notifications || false
  );
  
  // Form states
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Alert states
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  // Admin Thresholds
  const { isAdmin } = useIsAdmin();
  const [thresholdSpec, setThresholdSpec] = useState<Record<string, ThresholdSpec>>({});
  const [thresholdValues, setThresholdValues] = useState<Record<string, string>>({});
  const [thresholdsLoading, setThresholdsLoading] = useState(false);
  const [thresholdsSaving, setThresholdsSaving] = useState(false);
  const [sectionsJson, setSectionsJson] = useState('');
  const [sectionsDocId, setSectionsDocId] = useState('');
  const [sectionsSaving, setSectionsSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setThresholdsLoading(true);
    getAdminThresholds()
      .then(({ thresholds, spec }) => {
        if (cancelled) return;
        setThresholdSpec(spec);
        setThresholdValues(
          Object.fromEntries(Object.entries(thresholds).map(([k, v]) => [k, String(v)])),
        );
      })
      .catch(() => {
        if (!cancelled) showAlert('Failed to load thresholds', 'error');
      })
      .finally(() => {
        if (!cancelled) setThresholdsLoading(false);
      });
    getScoringConfig()
      .then((cfg) => {
        if (cancelled) return;
        setSectionsDocId(cfg.firestore_doc);
        setSectionsJson(JSON.stringify(cfg.sections, null, 2));
      })
      .catch(() => {
        /* thresholds path already alerts; full config is optional enrichment */
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const handleThresholdsSave = async () => {
    try {
      setThresholdsSaving(true);
      const updates: Record<string, number> = {};
      for (const [key, raw] of Object.entries(thresholdValues)) {
        const num = Number(raw);
        if (Number.isNaN(num)) {
          showAlert(`"${thresholdSpec[key]?.label || key}" must be a number`, 'error');
          setThresholdsSaving(false);
          return;
        }
        updates[key] = num;
      }
      const saved = await updateAdminThresholds(updates);
      setThresholdValues(
        Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, String(v)])),
      );
      showAlert('Thresholds updated successfully', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update thresholds';
      showAlert(msg, 'error');
    } finally {
      setThresholdsSaving(false);
    }
  };

  const handleSectionsSave = async () => {
    try {
      setSectionsSaving(true);
      const parsed = JSON.parse(sectionsJson) as Record<string, unknown>;
      const saved = await updateScoringConfig(parsed);
      setSectionsJson(JSON.stringify(saved.sections, null, 2));
      setSectionsDocId(saved.firestore_doc);
      if (saved.admin_thresholds) {
        setThresholdValues(
          Object.fromEntries(
            Object.entries(saved.admin_thresholds).map(([k, v]) => [k, String(v)]),
          ),
        );
      }
      showAlert('Full scoring config saved', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save scoring config';
      showAlert(msg, 'error');
    } finally {
      setSectionsSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isDark = event.target.checked;
    setDarkMode(isDark);
    // Dispatch theme change action
    dispatch(updateUserPreferences({ ui_theme: isDark ? 'dark' : 'light' }));
    
    showAlert('Theme updated successfully', 'success');
  };

  const handleNotificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setEnableNotifications(enabled);
    // Dispatch notifications change action
    dispatch(updateUserPreferences({ notifications: enabled }));
    
    showAlert('Notification settings updated', 'success');
  };

  const handleProfileUpdate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Dispatch profile update action
    // dispatch(updateUserProfile({ name, email }));
    
    showAlert('Profile updated successfully', 'success');
  };

  const handlePasswordUpdate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showAlert('New passwords do not match', 'error');
      return;
    }
    
    // Dispatch password update action
    // dispatch(updateUserPassword({ currentPassword, newPassword }));
    
    // Reset password fields
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    
    showAlert('Password updated successfully', 'success');
  };

  const showAlert = (message: string, severity: 'success' | 'error') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your account settings and preferences.
      </Typography>
      
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PersonIcon />} label="Profile" />
          <Tab icon={<PaletteIcon />} label="Appearance" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<SecurityIcon />} label="Security" />
          {isAdmin && <Tab icon={<TuneIcon />} label="Thresholds" />}
        </Tabs>
        
        {/* Profile Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box component="form" onSubmit={handleProfileUpdate} noValidate>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar 
                sx={{ width: 100, height: 100, mr: 3 }}
                src=""
                alt={user?.name}
              >
                {user?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Profile Picture
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="icon-button-file"
                  type="file"
                />
                <label htmlFor="icon-button-file">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CameraIcon />}
                  >
                    Change Photo
                  </Button>
                </label>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                >
                  Save Changes
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        
        {/* Appearance Tab */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Theme Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Customize the appearance of the application.
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={handleThemeChange}
                color="primary"
              />
            }
            label="Dark Mode"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {darkMode 
              ? 'Dark mode is enabled. The application will use a dark color scheme.' 
              : 'Light mode is enabled. The application will use a light color scheme.'}
          </Typography>
        </TabPanel>
        
        {/* Notifications Tab */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Notification Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Control how you receive notifications.
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={enableNotifications}
                onChange={handleNotificationsChange}
                color="primary"
              />
            }
            label="Enable Notifications"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {enableNotifications 
              ? 'You will receive notifications about processing status and updates.' 
              : 'Notifications are disabled. You will not receive alerts.'}
          </Typography>
        </TabPanel>
        
        {/* Security Tab */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Update your password to maintain account security.
          </Typography>
          
          <Box component="form" onSubmit={handlePasswordUpdate} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Update Password
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Thresholds Tab (admin only) */}
        {isAdmin && (
          <TabPanel value={activeTab} index={4}>
            <Typography variant="h6" gutterBottom>
              Scoring Thresholds
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Adjust deterministic scoring and display thresholds used by the Nuclius Index pipeline.
              Changes apply to new analyses once the rules cache refreshes
              {sectionsDocId ? ` (Firestore: config/${sectionsDocId})` : ''}.
            </Typography>

            {thresholdsLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading thresholds…
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {Object.entries(thresholdSpec).map(([key, spec]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField
                      fullWidth
                      type="number"
                      label={spec.label}
                      value={thresholdValues[key] ?? ''}
                      onChange={(e) =>
                        setThresholdValues((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      inputProps={{ min: spec.min, max: spec.max, step: 'any' }}
                      helperText={`Allowed range: ${spec.min}–${spec.max}`}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleThresholdsSave}
                    disabled={thresholdsSaving}
                  >
                    {thresholdsSaving ? 'Saving…' : 'Save Thresholds'}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Full scoring sections (JSON)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Edit pacing bands, scene-strength tables, originality axes, target-market
                    modifiers, and other nested rule tables. Save merges top-level section keys into
                    Firestore.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={16}
                    value={sectionsJson}
                    onChange={(e) => setSectionsJson(e.target.value)}
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    color="primary"
                    onClick={handleSectionsSave}
                    disabled={sectionsSaving || !sectionsJson}
                  >
                    {sectionsSaving ? 'Saving…' : 'Save Full Scoring Config'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        )}
      </Paper>
      
      <Snackbar 
        open={openAlert} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alertSeverity}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 
